'use client';

import Link from 'next/link';
import { useParams } from 'next/navigation';

import Text from '@/components/ui/Text';
import { ArticleDetail } from '@/lib/contentful/api/getArticle';
import { BLOG_I18N } from '@/lib/i18n/blogI18n';
import { resolveAppLocale } from '@/lib/i18n/routing';

type RelatedArticle = ArticleDetail['relatedArticles'][number];

const RelatedArticles = ({ articles }: RelatedArticlesProps) => {
  const { locale } = useParams<{ locale: string }>();
  const t = BLOG_I18N[resolveAppLocale(locale)];

  if (articles.length === 0) return null;

  return (
    <section className="mt-16 pt-8 border-t border-primary/20">
      <Text as="h2" variant="sectionEyebrow" className="mb-8">
        {t.relatedArticles}
      </Text>
      <div className="grid grid-cols-1 tablet:grid-cols-3 gap-8">
        {articles.map((article) => (
          <Link
            key={article.slug}
            href={`/${locale}/blog/${article.slug}`}
            className="group flex flex-col gap-2"
          >
            <Text
              as="h3"
              variant="cardTitle"
              className="group-hover:text-primary transition-colors line-clamp-2"
            >
              {article.title}
            </Text>
            <Text as="p" variant="paragraph" className="mb-0! text-sm">
              {article.date}
            </Text>
            <Text as="p" variant="paragraph" className="mb-0! line-clamp-3">
              {article.summary}
            </Text>
            <span className="mt-auto text-primary text-sm font-semibold group-hover:underline">
              {t.readArticle}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedArticles;

type RelatedArticlesProps = {
  articles: RelatedArticle[];
};
